import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Check, Star, Shield, ShoppingCart, ArrowUp, Sparkles, Building2, Mail, Users, TrendingUp, User, AlertCircle, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useTranslation } from "react-i18next";
import { useGlobalSubscription } from '@/hooks/useGlobalSubscription';
import { getPlanAction } from '@/utils/subscriptionHelpers';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import CheckoutAuthModal from '@/components/checkout/CheckoutAuthModal';
import { CompanyDataDialog } from "@/components/checkout/CompanyDataDialog";
import { motion, AnimatePresence } from "framer-motion";

interface PricingPlan {
  id?: string;
  plan_id: string;
  name: string;
  price_cents: number;
  currency: string;
  period: string;
  features: string[];
  is_active: boolean;
  is_purchasable: boolean;
  stripe_price_id?: string | null;
}

// Private plan prices
const PRIVATE_NEW_PRICES: Record<string, number> = {
  'basis': 499,
  'komfort': 999,
  'premium': 1499,
  'basis_year': 4999,
  'komfort_year': 9999,
  'premium_year': 14999,
};

const PRIVATE_OLD_PRICES: Record<string, number> = {
  'basis': 999,
  'komfort': 1999,
  'premium': 2499,
  'basis_year': 9999,
  'komfort_year': 19999,
  'premium_year': 24999,
};

// Business plan prices for upgrade calculation
const BUSINESS_PLAN_PRICES: Record<string, number> = {
  'basis': 999,
  'komfort': 1999,
  'premium': 2499,
  'company_basic': 1000,
  'company_multi_email': 1500,
  'company_pro': 2500,
  'company_basic_year': 9999,
  'company_multi_email_year': 14999,
  'company_pro_year': 24999,
  'none': 0
};

const PRIVATE_PLAN_IDS = ['basis', 'komfort', 'premium', 'basis_year', 'komfort_year', 'premium_year'];
const BUSINESS_PLAN_IDS = ['company_basic', 'company_basic_year', 'company_multi_email', 'company_multi_email_year', 'company_pro', 'company_pro_year'];
const ADDON_PLAN_IDS = ['phishing_basic', 'phishing_premium', 'email_check'];

const UnifiedPricing = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [customerType, setCustomerType] = useState<'private' | 'business'>('private');
  const [billingPeriod, setBillingPeriod] = useState<'month' | 'year'>('month');
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [addonPlans, setAddonPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [customerNumber, setCustomerNumber] = useState<string>('');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan | null>(null);
  
  // Use global subscription hook with caching - data is pre-loaded after login
  const { subscription, loading: subscriptionLoading, error: subscriptionError, isCompanyUser, isPrivateUser } = useGlobalSubscription();

  useEffect(() => {
    loadAllPlans();
  }, []);

  // Only fetch customerNumber when user is logged in and it's needed
  // This is cached after first load
  useEffect(() => {
    const loadCustomerNumber = async () => {
      if (!user || customerNumber) return;
      
      const { data, error } = await supabase
        .from('user_database')
        .select('customer_number')
        .eq('id', user.id)
        .single();
      
      if (!error && data) {
        setCustomerNumber(data.customer_number);
      }
    };
    
    loadCustomerNumber();
  }, [user, customerNumber]);

  const loadAllPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('pricing_plans')
        .select('*')
        .in('plan_id', [...PRIVATE_PLAN_IDS, ...BUSINESS_PLAN_IDS, ...ADDON_PLAN_IDS])
        .eq('is_active', true)
        .order('price_cents');
      
      if (error) throw error;
      
      const allPlans = (data || []).map(plan => {
        const isPrivatePlan = PRIVATE_PLAN_IDS.includes(plan.plan_id);
        return {
          ...plan,
          price_cents: isPrivatePlan 
            ? (PRIVATE_NEW_PRICES[plan.plan_id] || plan.price_cents)
            : plan.price_cents,
          is_purchasable: (plan as any).is_purchasable !== false,
          features: typeof plan.features === 'string' 
            ? JSON.parse(plan.features) 
            : Array.isArray(plan.features) 
              ? plan.features as string[]
              : []
        };
      });
      
      // Separate main plans from add-ons
      setPlans(allPlans.filter(p => !ADDON_PLAN_IDS.includes(p.plan_id)));
      setAddonPlans(allPlans.filter(p => ADDON_PLAN_IDS.includes(p.plan_id)));
    } catch (error) {
      console.error('Error loading pricing plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    return `${(cents / 100).toFixed(2).replace('.', ',')}`;
  };

  const isPopular = (planId: string) => {
    return planId === 'komfort' || planId === 'komfort_year';
  };

  const getButtonText = (planId: string, isBusinessPlan: boolean) => {
    const action = getPlanAction(subscription.plan_type || 'none', planId);
    
    switch (action) {
      case 'same':
        return 'Ihr aktueller Plan';
      case 'period_change':
        return billingPeriod === 'year' ? 'Zu jährlich wechseln' : 'Zu monatlich wechseln';
      case 'upgrade':
        return subscription.is_manual_subscription ? 'Kontaktieren Sie den Support' : 'Upgraden';
      case 'downgrade':
        return subscription.is_manual_subscription ? 'Kontaktieren Sie den Support' : 'Im Portal verwalten';
      default:
        return isBusinessPlan ? 'Jetzt abonnieren' : 'Jetzt testen für 1€';
    }
  };

  const isCurrentPlan = (planId: string) => {
    return subscription.plan_type === planId;
  };

  // Customer portal function
  const openCustomerPortal = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal');
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast({ title: 'Fehler beim Öffnen des Kundenportals', variant: 'destructive' });
    }
  };

  // Private plan handler
  const handlePrivatePlanClick = async (plan: PricingPlan) => {
    // Block private purchases for company customers
    if (isCompanyUser) {
      toast({
        title: 'Nicht verfügbar',
        description: 'Als Unternehmenskunde können Sie keine Privattarife buchen. Bitte wählen Sie einen Unternehmenstarif.',
        variant: 'destructive'
      });
      return;
    }

    const isFirstTimeSubscriber = !subscription.subscribed || subscription.plan_type === 'none';
    const isMonthlyPlan = !plan.plan_id.endsWith('_year');
    const hasTrialCoupon = ['basis', 'komfort', 'premium'].includes(plan.plan_id);
    const isTrialEligible = isFirstTimeSubscriber && isMonthlyPlan && hasTrialCoupon;
    
    if (!user) {
      window.dispatchEvent(
        new CustomEvent('addToCart', {
          detail: {
            id: plan.plan_id,
            name: plan.name,
            price: plan.price_cents,
            plan_id: plan.plan_id,
            period: billingPeriod,
            stripePriceId: plan.stripe_price_id,
            isTrialEligible: isTrialEligible,
          },
        })
      );
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setShowAuthModal(true);
      return;
    }

    if (subscription.subscribed) {
      const action = getPlanAction(subscription.plan_type, plan.plan_id);

      if (action === 'same') {
        toast({ title: t('pricing.alreadySubscribed') });
        return;
      }

      if (subscription.is_manual_subscription && (action === 'upgrade' || action === 'downgrade')) {
        const actionText = action === 'upgrade' ? 'Upgrade' : 'Downgrade';
        const subject = encodeURIComponent(`${actionText} Kundennummer ${customerNumber}`);
        const body = encodeURIComponent(
          `Guten Tag,\n\nich möchte gerne ein ${actionText} auf den Plan "${plan.name}" durchführen.\n\nMeine Kundennummer: ${customerNumber}\nGewünschter Plan: ${plan.name}\n\nMit freundlichen Grüßen`
        );
        window.location.href = `mailto:support@onlinesicher24.de?subject=${subject}&body=${body}`;
        return;
      }

      if (action === 'downgrade' && !subscription.is_manual_subscription) {
        try {
          const { data, error } = await supabase.functions.invoke('customer-portal');
          if (error) throw error;
          if (data?.error) throw new Error(data.error);
          if (data?.url) {
            window.open(data.url, '_blank');
          }
        } catch (error) {
          console.error('Error opening customer portal:', error);
          toast({ title: 'Fehler beim Öffnen des Kundenportals', variant: 'destructive' });
        }
        return;
      }
    }

    const action = subscription.subscribed ? getPlanAction(subscription.plan_type, plan.plan_id) : 'new';
    
    if (action === 'upgrade' || action === 'period_change') {
      try {
        await openCustomerPortal();
      } catch (error) {
        console.error('Error opening customer portal:', error);
        toast({ title: 'Fehler beim Öffnen des Kundenportals', variant: 'destructive' });
      }
      return;
    }
    
    window.dispatchEvent(
      new CustomEvent('addToCart', {
        detail: {
          id: plan.plan_id,
          name: plan.name,
          price: plan.price_cents,
          plan_id: plan.plan_id,
          period: billingPeriod,
          stripePriceId: plan.stripe_price_id,
          isTrialEligible: isTrialEligible,
        },
      })
    );
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Business plan handler
  const handleBusinessPlanClick = async (plan: PricingPlan) => {
    // Block business purchases for private customers with active subscription
    if (isPrivateUser) {
      toast({
        title: 'Nicht verfügbar',
        description: 'Als Privatkunde können Sie keine Unternehmenstarife buchen. Bitte kontaktieren Sie uns für einen Wechsel.',
        variant: 'destructive'
      });
      return;
    }

    const action = getPlanAction(subscription.plan_type || 'none', plan.plan_id);
    
    if (action === 'same') {
      toast({
        title: 'Bereits abonniert',
        description: 'Sie haben bereits diesen Plan.',
        variant: 'default'
      });
      return;
    }

    if (!user) {
      window.location.href = '/login';
      return;
    }

    const isUpgrade = action === 'upgrade';
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    window.dispatchEvent(new CustomEvent('addToCart', {
      detail: {
        id: plan.plan_id,
        name: plan.name,
        price: formatPrice(plan.price_cents),
        description: `${plan.name} - ${formatPrice(plan.price_cents)} € pro ${plan.period === 'month' ? 'Monat' : 'Jahr'}`,
        type: 'subscription',
        isUpgrade: isUpgrade,
        currentPlanPrice: isUpgrade ? BUSINESS_PLAN_PRICES[subscription.plan_type || 'none'] : undefined,
        period: plan.period,
        stripePriceId: plan.stripe_price_id
      }
    }));

    try {
      setProcessingPlan(plan.plan_id);
      
      const { data: companyProfile, error: profileError } = await supabase
        .from('company_profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (profileError) throw profileError;
      
      if (!companyProfile) {
        setSelectedPlan(plan);
        setCompanyDialogOpen(true);
        setProcessingPlan(null);
        return;
      }
      
      await proceedToCheckout(plan);
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast({
        title: 'Fehler beim Checkout',
        description: error.message || 'Bitte versuchen Sie es später erneut.',
        variant: 'destructive'
      });
      setProcessingPlan(null);
    }
  };

  const proceedToCheckout = async (plan: PricingPlan) => {
    try {
      setProcessingPlan(plan.plan_id);
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { 
          planType: plan.plan_id,
          stripePriceId: plan.stripe_price_id
        }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.url) {
        window.open(data.url, '_blank');
        toast({
          title: 'Checkout geöffnet',
          description: 'Bitte schließen Sie den Checkout-Prozess im neuen Tab ab.'
        });
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      toast({
        title: 'Fehler',
        description: error.message || 'Checkout konnte nicht gestartet werden.',
        variant: 'destructive'
      });
    } finally {
      setProcessingPlan(null);
    }
  };

  // Add-On handler
  const handleAddonClick = async (plan: PricingPlan) => {
    if (!user) {
      window.location.href = '/login';
      return;
    }

    try {
      setProcessingPlan(plan.plan_id);
      
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        body: { planType: plan.plan_id }
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      if (data?.url) {
        window.open(data.url, '_blank');
        toast({
          title: 'Checkout geöffnet',
          description: 'Bitte schließen Sie den Checkout-Prozess im neuen Tab ab.'
        });
      }
    } catch (error: any) {
      console.error('Add-on checkout error:', error);
      toast({
        title: 'Fehler',
        description: error.message || 'Checkout konnte nicht gestartet werden.',
        variant: 'destructive'
      });
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleCompanyDataSuccess = () => {
    if (selectedPlan) {
      proceedToCheckout(selectedPlan);
    }
  };

  // Error handling - show error instead of silent fallback
  if (subscriptionError && user) {
    return (
      <section id="preise" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Fehler beim Laden</AlertTitle>
              <AlertDescription>
                {subscriptionError.message}
                <br />
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => window.location.reload()}
                >
                  Seite neu laden
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        </div>
      </section>
    );
  }

  if (loading || subscriptionLoading) {
    return (
      <section id="preise" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold mb-4">{t('pricing.title')}</h2>
            <p className="text-xl text-muted-foreground">{t('pricing.loading')}</p>
          </div>
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </section>
    );
  }

  // Filter plans based on customer type and billing period
  const currentPlanIds = customerType === 'private' ? PRIVATE_PLAN_IDS : BUSINESS_PLAN_IDS;
  const displayedPlans = plans.filter(plan => {
    if (!currentPlanIds.includes(plan.plan_id)) return false;
    const isYearlyPlan = plan.plan_id.endsWith('_year');
    return billingPeriod === 'year' ? isYearlyPlan : !isYearlyPlan;
  });

  return (
    <section id="preise" className="py-20 bg-background relative">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-8">
          <h2 className="text-4xl font-bold mb-4">
            {customerType === 'private' ? t('pricing.title') : 'Unternehmenslösungen für E-Mail-Sicherheit'}
          </h2>
          <p className="text-xl text-muted-foreground">
            {customerType === 'private' 
              ? t('pricing.description')
              : 'Schützen Sie Ihr Team mit unserer fortschrittlichen E-Mail-Betrugsanalyse.'
            }
          </p>
        </div>

        {/* Customer Type Toggle */}
        <div className="flex justify-center mb-8">
          <Tabs value={customerType} onValueChange={(v) => setCustomerType(v as 'private' | 'business')} className="w-auto">
            <TabsList className="grid grid-cols-2 w-[400px] h-14">
              <TabsTrigger value="private" className="flex items-center gap-2 text-base py-3">
                <User className="w-5 h-5" />
                Privatkunde
              </TabsTrigger>
              <TabsTrigger value="business" className="flex items-center gap-2 text-base py-3">
                <Building2 className="w-5 h-5" />
                Unternehmenskunde
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Wrong Tab Info Banner */}
        <AnimatePresence mode="wait">
          {isCompanyUser && customerType === 'private' && (
            <motion.div
              key="company-user-wrong-tab"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="max-w-2xl mx-auto mb-6"
            >
              <Alert className="border-blue-500/50 bg-blue-50 dark:bg-blue-950/20">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-800 dark:text-blue-200">
                  Als Unternehmenskunde nutzen Sie bitte den{' '}
                  <button 
                    onClick={() => setCustomerType('business')} 
                    className="font-semibold underline hover:no-underline"
                  >
                    Unternehmenskunde-Tab
                  </button>
                  {' '}für passende Tarife.
                </AlertDescription>
              </Alert>
            </motion.div>
          )}
          {isPrivateUser && customerType === 'business' && (
            <motion.div
              key="private-user-wrong-tab"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="max-w-2xl mx-auto mb-6"
            >
              <Alert className="border-blue-500/50 bg-blue-50 dark:bg-blue-950/20">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <AlertDescription className="text-blue-800 dark:text-blue-200">
                  Als Privatkunde nutzen Sie bitte den{' '}
                  <button 
                    onClick={() => setCustomerType('private')} 
                    className="font-semibold underline hover:no-underline"
                  >
                    Privatkunde-Tab
                  </button>
                  {' '}für passende Tarife.
                </AlertDescription>
              </Alert>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Trial Banner - only for private monthly AND not already subscribed */}
        <AnimatePresence mode="wait">
          {customerType === 'private' && billingPeriod === 'month' && !subscription.subscribed && (
            <motion.div
              key="trial-banner"
              initial={{ opacity: 0, y: -20, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -20, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="max-w-2xl mx-auto mb-8 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border border-primary/20 rounded-2xl p-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Sparkles className="w-6 h-6 text-primary" />
                  <span className="text-2xl font-bold text-primary">Erster Monat nur 1€</span>
                  <Sparkles className="w-6 h-6 text-primary" />
                </div>
                <p className="text-muted-foreground">
                  Testen Sie OnlineSicher24 einen Monat lang für nur 1€. 
                  <br className="hidden sm:block" />
                  Danach faire Preise – jederzeit kündbar.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Billing Period Toggle */}
        <div className="flex flex-col items-center gap-2 mb-12">
          <div className="flex justify-center items-center gap-4">
            <span className={billingPeriod === 'month' ? 'font-semibold text-foreground' : 'text-muted-foreground'}>
              Monatlich
            </span>
            <Switch 
              checked={billingPeriod === 'year'} 
              onCheckedChange={(checked) => setBillingPeriod(checked ? 'year' : 'month')} 
            />
            <span className={billingPeriod === 'year' ? 'font-semibold text-foreground' : 'text-muted-foreground'}>
              Jährlich
            </span>
          </div>
          {billingPeriod === 'year' && (
            <Badge variant="secondary">17% sparen</Badge>
          )}
        </div>

        {/* Private Plans Grid */}
        <AnimatePresence mode="wait">
          {customerType === 'private' && displayedPlans.length > 0 && (
            <motion.div
              key="private-plans"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 30 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto"
            >
              {displayedPlans.map((plan, index) => {
                const action = getPlanAction(subscription.plan_type || 'none', plan.plan_id);
                const isActive = isCurrentPlan(plan.plan_id);
                const oldPrice = PRIVATE_OLD_PRICES[plan.plan_id];
                const isMonthly = !plan.plan_id.endsWith('_year');
                
                return (
                  <motion.div
                    key={plan.plan_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Card 
                      className={`shadow-soft hover:shadow-glow transition-all duration-300 relative flex flex-col h-full ${
                        isPopular(plan.plan_id) ? 'border-primary scale-105' : ''
                      } ${isActive ? 'border-2 border-green-500' : ''}`}
                    >
                      {isPopular(plan.plan_id) && !isActive && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-accent text-accent-foreground">
                          <Star className="w-3 h-3 mr-1" />
                          {t('pricing.popular')}
                        </Badge>
                      )}
                      {isActive && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white">
                          ✓ Aktiv
                        </Badge>
                      )}
                    
                      <CardHeader className="text-center pb-2">
                        <CardTitle className="text-2xl">{plan.name}</CardTitle>
                        <div className="mt-4">
                          {oldPrice && oldPrice !== plan.price_cents && (
                            <div className="text-muted-foreground line-through text-lg">
                              €{formatPrice(oldPrice)}
                            </div>
                          )}
                          
                          <span className="text-4xl font-bold">€{formatPrice(plan.price_cents)}</span>
                          <span className="text-muted-foreground">/{plan.period === 'month' ? t('pricing.period.month') : t('pricing.period.year')}</span>
                          
                          {oldPrice && oldPrice !== plan.price_cents && (
                            <Badge className="ml-2 bg-red-500 text-white">
                              -{Math.round((1 - plan.price_cents / oldPrice) * 100)}%
                            </Badge>
                          )}
                        </div>
                        {plan.period === 'year' && (
                          <Badge className="mt-2 bg-green-600 text-white w-fit mx-auto">
                            2 Monate geschenkt!
                          </Badge>
                        )}
                      </CardHeader>
                      
                      <CardContent className="flex flex-col flex-1">
                        <ul className="space-y-3 mb-6 flex-1">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <div className="mt-auto">
                          <Button 
                            variant={!plan.is_purchasable ? "outline" : action === 'upgrade' ? "hero" : isPopular(plan.plan_id) ? "hero" : "default"} 
                            size="xl" 
                            className="w-full text-lg py-4"
                            onClick={() => handlePrivatePlanClick(plan)}
                            disabled={action === 'same' || isCompanyUser || !plan.is_purchasable}
                          >
                            {!plan.is_purchasable ? (
                              'Coming soon...'
                            ) : (
                              <>
                                {action === 'upgrade' && !isCompanyUser && <ArrowUp className="mr-2 h-5 w-5" />}
                                {action !== 'upgrade' && action !== 'new' && !isCompanyUser && <ShoppingCart className="mr-2 h-5 w-5" />}
                                {action === 'new' && isMonthly && !isCompanyUser && <Sparkles className="mr-2 h-5 w-5" />}
                                {isCompanyUser ? 'Nicht für Unternehmen' : getButtonText(plan.plan_id, false)}
                              </>
                            )}
                          </Button>
                          {action === 'upgrade' && !subscription.is_manual_subscription && (
                            <p className="text-xs text-muted-foreground text-center mt-2">
                              Sie zahlen nur die anteilige Differenz
                            </p>
                          )}
                          {((action === 'upgrade' && subscription.is_manual_subscription) || 
                            (action === 'downgrade' && subscription.is_manual_subscription)) && !isCompanyUser && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 text-center mt-2">
                              Unser Team meldet sich bei Ihnen
                            </p>
                          )}
                          {action === 'period_change' && (
                            <p className="text-xs text-green-600 dark:text-green-400 text-center mt-2">
                              Sofortiger Wechsel • {billingPeriod === 'year' ? '17% sparen' : 'Flexibel bleiben'}
                            </p>
                          )}
                          {action === 'new' && isMonthly && (
                            <p className="text-xs text-primary text-center mt-2">
                              Danach {formatPrice(plan.price_cents)}€/Monat • Jederzeit kündbar
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {/* Business Plans Grid */}
          {customerType === 'business' && displayedPlans.length > 0 && (
            <motion.div
              key="business-plans"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-12"
            >
              {displayedPlans.map((plan, index) => {
                const action = getPlanAction(subscription.plan_type || 'none', plan.plan_id);
                const isActive = isCurrentPlan(plan.plan_id);
                
                return (
                  <motion.div
                    key={plan.plan_id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Card 
                      className={`shadow-soft hover:shadow-glow transition-all duration-300 flex flex-col h-full relative ${
                        isActive ? 'border-2 border-green-500' : ''
                      }`}
                    >
                      {isActive && (
                        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-green-500 text-white">
                          ✓ Aktiv
                        </Badge>
                      )}
                      <CardHeader className="text-center pb-6">
                        <div className="mx-auto mb-4 p-3 rounded-full bg-primary/10 w-fit">
                          <Building2 className="w-8 h-8 text-primary" />
                        </div>
                        <CardTitle className="text-3xl">{plan.name}</CardTitle>
                        <CardDescription className="text-base mt-2">
                          Ideal für kleine Teams und Einzelunternehmer
                        </CardDescription>
                        <div className="mt-6">
                          <span className="text-5xl font-bold">€{formatPrice(plan.price_cents)}</span>
                          <span className="text-muted-foreground text-lg">/{plan.period === 'month' ? 'Monat' : 'Jahr'}</span>
                        </div>
                        {plan.period === 'year' && (
                          <Badge className="mt-2 bg-green-600 text-white w-fit mx-auto">
                            2 Monate geschenkt!
                          </Badge>
                        )}
                      </CardHeader>
                      
                      <CardContent className="flex flex-col flex-grow">
                        <ul className="space-y-4 mb-8 text-center flex-grow">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center justify-center gap-3">
                              <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                              <span className="text-base">{feature}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <Button 
                          variant={!plan.is_purchasable ? "outline" : action === 'upgrade' && !isPrivateUser ? "hero" : "default"}
                          size="lg" 
                          className="w-full text-lg py-6"
                          onClick={() => handleBusinessPlanClick(plan)}
                          disabled={processingPlan === plan.plan_id || action === 'same' || isPrivateUser || !plan.is_purchasable}
                        >
                          {!plan.is_purchasable ? (
                            'Coming soon...'
                          ) : processingPlan === plan.plan_id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                              Wird geladen...
                            </>
                          ) : isPrivateUser ? (
                            'Nicht für Privatkunden'
                          ) : (
                            <>
                              {action === 'upgrade' ? (
                                <ArrowUp className="mr-2 h-5 w-5" />
                              ) : (
                                <Shield className="mr-2 h-5 w-5" />
                              )}
                              {getButtonText(plan.plan_id, true)}
                            </>
                          )}
                        </Button>
                        {action === 'upgrade' && (
                          <p className="text-xs text-muted-foreground text-center mt-2">
                            Sie zahlen nur die anteilige Differenz
                          </p>
                        )}
                        
                        <p className="text-center text-sm text-muted-foreground mt-4">
                          Für größere Teams mit mehr E-Mail-Adressen? 
                          <a href="tel:+4915258970017" className="text-primary hover:underline ml-1">
                            Kontaktieren Sie uns
                          </a>
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Business Features Section */}
        {customerType === 'business' && (
          <div className="max-w-4xl mx-auto mt-12">
            <Card className="bg-card/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-center text-2xl">Perfekt für Ihr Unternehmen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">KI-gestützte Analyse</h3>
                      <p className="text-sm text-muted-foreground">
                        Modernste Betrugserkennungstechnologie
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Mail className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">E-Mail-Integration</h3>
                      <p className="text-sm text-muted-foreground">
                        Leiten Sie verdächtige E-Mails einfach nur weiter und kriegen sie eine Antwort innerhalb von Sekunden.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Users className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Team-Management</h3>
                      <p className="text-sm text-muted-foreground">
                        Zentrale E-Mail-Verwaltung
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <TrendingUp className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">Monatliche Reports</h3>
                      <p className="text-sm text-muted-foreground">
                        Regelmäßige Sicherheitsberichte
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Private Plan Guarantees */}
        {customerType === 'private' && (
          <div className="text-center mt-12 space-y-4">
            <div className="flex items-center justify-center gap-4 flex-wrap text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-success" />
                <span>{t('pricing.guarantees.moneyBack')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-success" />
                <span>{t('pricing.guarantees.cancellable')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-success" />
                <span>{t('pricing.guarantees.noSetupFees')}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      <CheckoutAuthModal 
        open={showAuthModal} 
        onOpenChange={setShowAuthModal}
        onSuccess={() => {}}
      />

      <CompanyDataDialog
        open={companyDialogOpen}
        onOpenChange={setCompanyDialogOpen}
        onSuccess={handleCompanyDataSuccess}
      />
    </section>
  );
};

export default UnifiedPricing;
